import { useCreateVideoStreamMutation, useUpdateVideoStreamMutation } from '@shared/api/graphql/generated';
import { toast } from '@shared/ui';
import { useState } from 'react';
import { IDraggableVideoMediafile, IVideoMediafile, IVideoStreamEmenuMediafile, VideoStreamFlow } from '../types';
import { TEXTS } from '../texts';
import { getDefaultVideoStreamInput } from './utils';

interface UseCreateVideoStreamController {
  createVideoStream: ReturnType<typeof useCreateVideoStreamMutation>[0];
  updateVideoStream: ReturnType<typeof useUpdateVideoStreamMutation>[0];
  title?: string;
  description?: string;
  flow: VideoStreamFlow;
  selectedFiles: IVideoMediafile[];
  emenuMediafiles?: IVideoStreamEmenuMediafile[];
  videoStreamId?: string;
  points?: { id: string }[];
  companyId: string;
}

export const useCreateVideoStreamController = ({
  createVideoStream,
  updateVideoStream,
  companyId,
  title = '',
  description = '',
  selectedFiles,
  emenuMediafiles = [],
  flow,
  videoStreamId = '',
}: UseCreateVideoStreamController) => {
  const [mediafiles, setMediafiles] = useState<IDraggableVideoMediafile[]>(
    selectedFiles.map((it, i) => ({ ...it, ...it.mediafile, id: `${it.id}-${i}` }))
  );

  const [emenuOrder, setEmenuOrder] = useState<IVideoStreamEmenuMediafile[]>(emenuMediafiles);

  const setMediafilePlayDuration = (id?: string | null, playDuration?: number) => {
    if (!id) return;
    setMediafiles((current) =>
      current.map((it) => (it.id === id ? { ...it, playDuration, draftPlayDuration: undefined } : it))
    );
  };

  const setMediafileDraftDuration = (id: string, draftPlayDuration: number) => {
    setMediafiles((current) => current.map((it) => (it.id === id ? { ...it, draftPlayDuration } : it)));
  };

  const mediafilesToSave = mediafiles.map((it) => ({ ...it, playDuration: it.draftPlayDuration ?? it.playDuration }));

  const removeMediafile = (id: string) => {
    setMediafiles(mediafiles.filter((it) => it.id !== id));
  };

  const [videoStreamTitle, setVideoStreamTitle] = useState(title);
  const [videoStreamDescription, setVideoStreamDescription] = useState(description);

  const modalTitle = flow === VideoStreamFlow.CREATE ? TEXTS.CREATE_VIDEO_STREAM_TITLE : TEXTS.EDIT_VIDEO_STREAM_TITLE;

  const onCreate = async (title: string, description: string, videoMediafiles: IVideoMediafile[]) => {
    try {
      const commonInput = getDefaultVideoStreamInput(title, description, videoMediafiles, emenuOrder);

      const input = {
        ...commonInput,
        companyId,
        pointIds: [],
      };

      const { errors, data } = await createVideoStream({
        variables: {
          input,
        },
      });

      if (errors?.length) {
        throw new Error(errors[0].message);
      }
      if (data?.result?.ok === false || !data?.result?.videoStream?.id) {
        throw new Error(data?.result?.errors?.[0]?.messages.join(' ') || TEXTS.VIDEO_STREAM_SAVE_ERROR);
      }
      return data.result.videoStream.id;
    } catch (error) {
      toast.error((error as Error).message);
    }
    return undefined;
  };

  const copyMediafile = (id: string) => {
    setMediafiles((currentMediafiles) => {
      const index = currentMediafiles.findIndex((it) => it.id === id);
      if (index < 0) return currentMediafiles;
      const newMediafiles = [...currentMediafiles];
      newMediafiles.splice(index + 1, 0, {
        ...currentMediafiles[index],
        playDuration: currentMediafiles[index].draftPlayDuration ?? currentMediafiles[index].playDuration,
        draftPlayDuration: undefined,
        isCopy: true,
      });
      return newMediafiles.map((it, i) => ({ ...it, id: `${it.id.replace(/-.*/, '')}-${i}` }));
    });
  };

  const onUpdate = async (title: string, description: string, videoMediafiles: IVideoMediafile[]) => {
    try {
      const commonInput = getDefaultVideoStreamInput(title, description, videoMediafiles, emenuOrder);

      const input = {
        ...commonInput,
        videoStreamId,
      };

      const { errors, data } = await updateVideoStream({
        variables: {
          input,
        },
      });

      if (errors?.length) {
        throw new Error(errors[0].message);
      }
      if (data?.result?.ok === false || !data?.result?.videoStream?.id) {
        throw new Error(data?.result?.errors?.[0]?.messages.join(' ') || TEXTS.VIDEO_STREAM_SAVE_ERROR);
      }
      return data.result.videoStream.id;
    } catch (error) {
      toast.error((error as Error).message);
    }
    return undefined;
  };

  return {
    onSave: flow === VideoStreamFlow.CREATE ? onCreate : onUpdate,
    mediafiles,
    mediafilesToSave,
    emenuOrder,
    setMediafiles,
    setEmenuOrder,
    videoStreamTitle,
    setVideoStreamTitle,
    videoStreamDescription,
    setVideoStreamDescription,
    modalTitle,
    setMediafilePlayDuration,
    setMediafileDraftDuration,
    removeMediafile,
    copyMediafile,
  };
};
